// FlixPreview.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Observer from '@researchgate/react-intersection-observer';

import SwagLoading from '../component/SwagLoading.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import LazyImage from '../component/LazyImage.jsx';
import TrailerBlocker from '../container/TrailerBlocker.js';
import { getIsOnMobile } from '../resource/getUserAgent.js';
import { formatTimeString } from '../resource/formatTimeString.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import NativeVideoPlayer from '../container/NativeVideoPlayer.js';
import FlixBadge from '../container/FlixBadge.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import { ProfileListType } from '../resource/profileConstants.js';
import { archiveType } from '../resource/archiveConstants.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

export class FlixPreview extends React.PureComponent {
  state = {
    shouldAutoPlay: false,
    isHovered: false,
    isVideoOnloaded: false,
    isIntersecting: false,
  };
  nextTicks = [];

  componentDidMount() {
    const { userId, username, fetchUser } = this.props;
    if (userId && !username) {
      this.nextTicks.push(setTimeout(() => fetchUser(userId)));
    }
    this.fetchAssetManifest();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      messageId,
      assetIds,
      duration,
      shouldPlay,
      isAuthed,
      userId,
      username,
      fetchUser,
    } = this.props;
    const { isIntersecting } = this.state;
    if (shouldPlay !== prevProps.shouldPlay) {
      if (shouldPlay) {
        this.autoPlayTimeout = setTimeout(
          () => this.setState({ shouldAutoPlay: true }),
          300
        );
      } else {
        this.autoPlayTimeout && clearTimeout(this.autoPlayTimeout);
        this.setState({ shouldAutoPlay: false });
      }
    }
    if (isAuthed !== prevProps.isAuthed || userId !== prevProps.userId) {
      if (userId && !username) {
        this.nextTicks.push(setTimeout(() => fetchUser(userId)));
      }
    }
    if (
      messageId !== prevProps.messageId ||
      assetIds !== prevProps.assetIds ||
      duration !== prevProps.duration ||
      isIntersecting !== prevState.isIntersecting
    ) {
      this.fetchAssetManifest();
    }
  }

  componentWillUnmount() {
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    if (this.autoPlayTimeout) clearTimeout(this.autoPlayTimeout);
    this.nextTicks.forEach(clearTimeout);
  }

  fetchAssetManifest = () => {
    const { messageId, contentType, duration, assetIds, fetchAssetManifest } =
      this.props;
    const { isIntersecting } = this.state;
    const assetId = assetIds[0];
    const isVideo = contentType.startsWith('video/');
    if (duration || !isIntersecting || !messageId || !assetId || !isVideo) {
      return;
    }
    return fetchAssetManifest({ messageId, assetId });
  };

  handleOnMouseEnter = () => {
    this.mouseTimeout = setTimeout(
      () => this.setState({ isHovered: true }),
      300
    );
  };

  handleOnMouseLeave = () => {
    if (this.mouseTimeout) clearTimeout(this.mouseTimeout);
    this.setState({ isHovered: false });
  };

  handleVideoOnloaded = () => {
    this.setState({ isVideoOnloaded: true });
  };
  trackUserClick = () => {
    if (this.props.shouldTrackRecommendEvent) {
      const { messageId } = this.props;
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: ButtonId.Detail.ButtonRecommendFlix,
          extraProps: {
            'recommend.message.id': messageId,
          },
        },
      });
    }
    if (this.props.shouldTrackCardEvent) {
      const { categoryIndex, categoryId, username, messageId } = this.props;
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: ButtonId.All.ButtonFlixCard,
          messageId,
          extraProps: {
            'user.username': username,
            'discover.index': categoryIndex,
            'discover.category': categoryId,
          },
        },
      });
    }
    const { shouldTrackProfilePostEvent, messageId } = this.props;
    if (shouldTrackProfilePostEvent) {
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId: ButtonId.Profile.ButtonPostClick,
          messageId,
        },
      });
    }
  };
  renderUsernameTooltip = () => {
    const { username } = this.props;

    return <TooltipWrapper>{username}</TooltipWrapper>;
  };

  fillUpContainer = ({ videoWidth, videoHeight }) => {
    const targetRatio = 16 / 9;
    const currentRatio = videoWidth / videoHeight;

    if (currentRatio >= targetRatio) {
      this.setState({
        playerStyle: {
          flex: 'none',
          width: 'auto',
          height: '100%',
        },
      });
    } else if (currentRatio < targetRatio) {
      this.setState({
        playerStyle: {
          // flex: 'none',
          width: '100%',
          height: 'auto',
        },
      });
    }
  };

  handleIntersection = ({ isIntersecting }, unobserve) => {
    this.setState({ isIntersecting });
    if (isIntersecting) {
      unobserve();
    }
  };

  handleLoadedMetadata = (event, player) => {
    const videoWidth = player.videoWidth;
    const videoHeight = player.videoHeight;

    this.fillUpContainer({ videoWidth, videoHeight });
  };

  handleLinkClick = e => {
    const { shouldUseLink, messageId } = this.props;
    this.trackUserClick();
    // For pages that needs to push to history stack, use with shouldUseLink, otherwise replace the history.
    // For example, from / to /flix/{messageId}, use Link (shouldUseLink: true)
    // From /flix/{messageId1} to /flix/{messageId2}, use replace (shouldUseLink: false)
    if (shouldUseLink) return;
    e.preventDefault();
    this.props.replace(`/post/${messageId}`);
  };

  renderVideo = () => {
    const { messageId, title } = this.props;
    const { playerStyle } = this.state;

    return (
      <VideoWrapper>
        <VideoContainer playerStyle={playerStyle}>
          <NativeVideoPlayer
            isMuted
            isLoop
            messageId={messageId}
            alt={title}
            onLoadedData={this.handleVideoOnloaded}
            onLoadedMetadata={this.handleLoadedMetadata}
          />
        </VideoContainer>
      </VideoWrapper>
    );
  };
  getShouldRenderVideo = () => {
    const { drmRulesetTeststatus, shouldPlay } = this.props;
    const { isHovered } = this.state;
    const shouldRenderVideo =
      drmRulesetTeststatus !== drmRulesetTestStatusConstant.TESTING &&
      (shouldPlay || (isHovered && !getIsOnMobile()));
    return shouldRenderVideo;
  };
  getLocationState = () => {
    const {
      contentType,
      userId,
      shortVideoOrigin,
      duration,
      shortVideoDuration,
    } = this.props;
    const isVideo = contentType.startsWith('video/');
    const isShortVideo =
      shortVideoDuration && isVideo && duration < shortVideoDuration;
    if (!isShortVideo) {
      return {};
    }
    if ('profile' === shortVideoOrigin) {
      return {
        listPath: ['outbox', ProfileListType.SHORT, userId],
      };
    }
    if ('archive' === shortVideoOrigin) {
      return {
        listPath: ['unlocked', 'messages', archiveType.SHORT, userId],
      };
    }
    return {};
  };

  renderThumbnail() {
    const { isVideoOnloaded } = this.state;
    const {
      messageId,
      thumbnailSource,
      username,
      title,
      shouldAnimateLoader,
      shouldShowLoader,
      shouldUseLazyImage,
    } = this.props;
    const shouldRenderVideo = this.getShouldRenderVideo();
    if (!messageId) {
      return (
        <ImageWrapper shouldShowVideo={shouldRenderVideo && isVideoOnloaded}>
          {shouldShowLoader && (
            <SwagLoadingWrapper>
              <SwagLoading shouldAnimate={shouldAnimateLoader} />
            </SwagLoadingWrapper>
          )}
        </ImageWrapper>
      );
    }
    const jpgSrcSet = getPublicSrcSet({
      href: thumbnailSource,
      size: 256,
      format: MediaAssetFormat.JPG,
    });
    const sources = [MediaAssetFormat.AVIF, MediaAssetFormat.WEBP].map(
      format => {
        const srcSet = getPublicSrcSet({
          href: thumbnailSource,
          size: 256,
          format,
        });
        return <source key={format} {...srcSet} type={`image/${format}`} />;
      }
    );
    return (
      <ImageWrapper shouldShowVideo={shouldRenderVideo && isVideoOnloaded}>
        <StatefulImage>
          <StatefulImageWrapper data-key="loading">
            {shouldShowLoader && (
              <SwagLoadingWrapper>
                <SwagLoading shouldAnimate={shouldAnimateLoader} />
              </SwagLoadingWrapper>
            )}
          </StatefulImageWrapper>
          {shouldUseLazyImage ? (
            <LazyImage
              data-key="target"
              threshold={0}
              alt={`${username} : ${title}`}
              {...jpgSrcSet}
            >
              {sources}
            </LazyImage>
          ) : (
            <Picture data-key="target">
              {sources}
              <img {...jpgSrcSet} alt={`${username} : ${title}`} />
            </Picture>
          )}
        </StatefulImage>
      </ImageWrapper>
    );
  }

  renderBadge = () => {
    const { badges, messageBadgeNotShowList } = this.props;
    return (
      <BadgeWrapper>
        {badges
          .filter(value => !messageBadgeNotShowList.includes(value))
          .map(value => (
            <FlixBadge key={value} badgeName={value} />
          ))}
      </BadgeWrapper>
    );
  };

  render() {
    const { messageId, duration, style, isOnFreezone, hasFreePeek } =
      this.props;

    const LinkProps = {
      to:
        isOnFreezone && hasFreePeek
          ? `/freezone/${messageId}`
          : `/post/${messageId}`,

      onClick: this.handleLinkClick,
      as: Link,
      state: this.getLocationState(),
      onMouseOver: this.handleOnMouseEnter,
      onMouseOut: this.handleOnMouseLeave,
    };

    return (
      <Observer onChange={this.handleIntersection}>
        <StyledFlixPreview {...(messageId ? LinkProps : {})} style={style}>
          {this.renderBadge()}
          {this.renderThumbnail()}
          {this.getShouldRenderVideo() && this.renderVideo()}
          <TrailerBlocker zIndex="1" />
          {!!duration && (
            <Duration>{formatTimeString(duration * 1000)}</Duration>
          )}
        </StyledFlixPreview>
      </Observer>
    );
  }
}

FlixPreview.propTypes = {
  messageId: PropTypes.string,
  categoryId: PropTypes.string,
  shortVideoOrigin: PropTypes.string,
  thumbnailSource: PropTypes.string,
  drmRulesetTeststatus: PropTypes.string,
  contentType: PropTypes.string,
  categoryIndex: PropTypes.number,
  duration: PropTypes.number,
  shortVideoDuration: PropTypes.number,
  isAuthed: PropTypes.bool,
  shouldShowLoader: PropTypes.bool,
  shouldAnimateLoader: PropTypes.bool,
  shouldUseLink: PropTypes.bool,
  shouldUseLazyImage: PropTypes.bool,
  shouldTrackCardEvent: PropTypes.bool,
  shouldTrackRecommendEvent: PropTypes.bool,
  shouldTrackProfilePostEvent: PropTypes.bool,
  shouldPlay: PropTypes.bool,
  hasFreePeek: PropTypes.bool,
  isOnFreezone: PropTypes.bool,
  replace: PropTypes.func,
  fetchUser: PropTypes.func,
  fetchAssetManifest: PropTypes.func,
  userId: PropTypes.string,
  username: PropTypes.string,
  title: PropTypes.string,
  messageBadgeNotShowList: PropTypes.array,
  badges: PropTypes.array,
  assetIds: PropTypes.array,
  style: PropTypes.object,
};

FlixPreview.defaultProps = {
  categoryId: null,
  shortVideoOrigin: '',
  thumbnailSource: '',
  drmRulesetTeststatus: '',
  contentType: '',
  categoryIndex: null,
  duration: null,
  shortVideoDuration: null,
  shouldPlay: false,
  hasFreePeek: false,
  isOnFreezone: false,
  messageId: '',
  isAuthed: false,
  shouldUseLink: false,
  shouldTrackCardEvent: false,
  shouldTrackRecommendEvent: false,
  shouldUseLazyImage: true,
  shouldTrackProfilePostEvent: false,
  messageBadgeNotShowList: [],
  badges: [],
  assetIds: [],
  replace: () => null,
  fetchUser: () => null,
  fetchAssetManifest: () => null,
  userId: '',
  username: '',
  title: '',
};

const VideoWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: 100%;
  width: 100%;
  z-index: -1;
`;

const VideoContainer = styled.div`
  ${({ playerStyle }) => playerStyle}
`;

const StyledFlixPreview = styled.div`
  display: block;
  position: relative;
  padding-bottom: 56%;
  width: 100%;
  overflow: hidden;
  transform: translateZ(0);
  z-index: 1;
  border-radius: 4px;

  &:hover {
    opacity: 1;
  }

  ${media.mobile`
    padding-bottom: 64%;
  `};
`;

const BadgeWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  z-index: 1;
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${({ shouldShowVideo }) =>
    shouldShowVideo ? 'opacity 2s' : 'none'};
  opacity: ${({ shouldShowVideo }) => (shouldShowVideo ? 0 : 1)};
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: ${color.neutral[10]};
`;

const StatefulImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  & > img {
    width: 80%;
  }
`;

const Duration = styled.span`
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  padding: 2px 4px;
  border-radius: 4px;

  position: absolute;
  z-index: 3;
  bottom: 8px;
  right: 8px;
`;

const TooltipWrapper = styled.div`
  margin-top: 2px;
  padding: 4px;
  border-radius: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  background-color: rgb(246, 246, 246, 0.8);
  color: #191919;
  font-size: 12px;
  line-height: 1;
`;

const SwagLoadingWrapper = styled.div`
  width: 120px;
`;

const Picture = styled.picture`
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default FlixPreview;
